import { supabase } from '@/lib/supabase';
import { getCurrentTenantId } from '@/utils/tenantUtils';

export interface ProjectWebhook {
  id: string;
  name: string;
  description?: string;
  url: string;
  events: string[];
  stages?: string[];
  statuses?: string[];
  project_types?: string[];
  secret?: string;
  is_active: boolean;
  created_at: string;
  created_by: string;
}

export interface CreateWebhookInput {
  name: string;
  description?: string;
  url: string;
  events: string[];
  stages?: string[];
  statuses?: string[];
  project_types?: string[];
  secret?: string;
}

class WebhookService {
  private static instance: WebhookService;
  private initialized = false;

  private constructor() {
    this.initialized = true;
  }

  public static getInstance(): WebhookService {
    if (!WebhookService.instance) {
      WebhookService.instance = new WebhookService();
    }
    return WebhookService.instance;
  }

  async getWebhooks(): Promise<ProjectWebhook[]> {
    if (!this.initialized) {
      console.error('WebhookService not initialized');
      return [];
    }

    try {
      // Get current tenant ID to filter webhooks
      const tenantId = await getCurrentTenantId();
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      const { data, error } = await supabase
        .from('project_webhooks')
        .select('*')
        .eq('tenant_id', tenantId);

      if (error) {
        throw new Error('Failed to fetch webhooks');
      }

      return (data || []) as unknown as ProjectWebhook[];
    } catch (error) {
      console.error('Error in getWebhooks:', error);
      throw error;
    }
  }

  async createWebhook(input: CreateWebhookInput): Promise<ProjectWebhook> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to create webhooks');
    }

    if (!input.name) {
      throw new Error('Webhook name is required');
    }
    if (!input.url) {
      throw new Error('Webhook URL is required');
    }
    if (!input.events || input.events.length === 0) {
      throw new Error('At least one event must be selected');
    }

    try {
      const tenantId = await getCurrentTenantId();
      
      const { data, error } = await supabase
        .from('project_webhooks')
        .insert({
          name: input.name,
          description: input.description,
          url: input.url,
          events: input.events,
          stages: input.stages || [],
          statuses: input.statuses || [],
          project_types: input.project_types || [],
          secret: input.secret || null,
          is_active: true,
          created_by: user.id,
          tenant_id: tenantId
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create webhook: ${error.message}`);
      }

      if (!data) {
        throw new Error('No data returned after creating webhook');
      }

      return data as unknown as ProjectWebhook;
    } catch (error) {
      console.error('Error creating webhook:', error);
      throw error;
    }
  }

  async deleteWebhook(id: string): Promise<void> {
    // Get current tenant ID for security verification
    const tenantId = await getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }

    const { error } = await supabase
      .from('project_webhooks')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId);

    if (error) throw error;
  }

  async updateWebhook(id: string, updates: Partial<ProjectWebhook>): Promise<void> {
    // Get current tenant ID for security verification
    const tenantId = await getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }

    const { error } = await supabase
      .from('project_webhooks')
      .update(updates)
      .eq('id', id)
      .eq('tenant_id', tenantId);

    if (error) throw error;
  }

  async triggerWebhooks(event: string, payload: any): Promise<void> {
    if (!this.initialized) {
      console.error('WebhookService not initialized');
      return;
    }

    try {
      const webhooks = await this.getWebhooks();
      
      if (!webhooks || webhooks.length === 0) {
        return;
      }
      
      // Filter webhooks that listen for this specific event
      const relevantWebhooks = webhooks.filter(webhook => 
        webhook.events.includes(event)
      );
      
      if (relevantWebhooks.length === 0) {
        return;
      }
      
      for (const webhook of relevantWebhooks) {
        if (!webhook.is_active) {
          continue;
        }

        let shouldTrigger = true;

        // Handle project creation events
        if (event === 'project_created') {
          // Check project type if specified
          if (webhook.project_types?.length) {
            shouldTrigger = webhook.project_types.some(type => 
              type.toLowerCase() === payload.project_type.toLowerCase()
            );
          }
        }
        // Handle stage change events
        else if (event === 'stage_change' && webhook.stages?.length) {
          const { data: stageData, error: stageError } = await supabase
            .from('stages')
            .select('id')
            .in('name', webhook.stages);

          if (stageError) {
            console.error('Error fetching stages:', stageError);
            continue;
          }

          const stageIds = stageData.map(stage => stage.id);
          shouldTrigger = stageIds.includes(payload.new_stage_id);
        }

        if (shouldTrigger) {
          try {
            // Queue the webhook
            const tenantId = await getCurrentTenantId();
            
            const { error: queueError } = await supabase
              .from('webhook_queue')
              .insert({
                webhook_id: webhook.id,
                event,
                payload,
                status: 'pending',
                tenant_id: tenantId
              });

            if (queueError) {
              console.error('Error queueing webhook:', queueError);
              throw queueError;
            }
          } catch (error) {
            console.error(`Error queueing webhook for ${webhook.url}:`, error);
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error triggering webhooks:', error);
      throw error;
    }
  }

  async testOnboardingWebhooks(): Promise<void> {
    try {
      // Test onboarding form submission
      await this.triggerWebhooks('onboarding_form_submitted', {
        form_id: 'test-form-123',
        user_email: 'test@example.com',
        user_name: 'Test User',
        company_name: 'Test Company',
        phone: '+1234567890',
        project_type: 'residential',
        budget_range: '$50,000 - $100,000',
        timeline: '3-6 months',
        status: 'lead',
        created_at: new Date().toISOString(),
        metadata: {
          source: 'test',
          test: true
        }
      });

      // Test onboarding booking creation
      await this.triggerWebhooks('onboarding_booking_created', {
        booking_id: 'test-booking-456',
        user_email: 'test@example.com',
        user_name: 'Test User',
        company_name: 'Test Company',
        phone: '+1234567890',
        booking_date: '2024-01-15T10:00:00Z',
        booking_duration: 30,
        meeting_type: 'video_call',
        status: 'onboarding-booked',
        created_at: new Date().toISOString(),
        metadata: {
          source: 'test',
          test: true
        }
      });
    } catch (error) {
      console.error('Error testing onboarding webhooks:', error);
      throw error;
    }
  }

  async sendProjectCreated(project: any): Promise<void> {
    await this.triggerWebhooks('project_created', {
      event: 'project_created',
      project_id: project.id,
      project_name: project.project_name,
      customer_name: project.customer_name,
      customer_email: project.customer_email,
      system_size: project.system_size,
      system_cost: project.system_cost,
      project_status: project.project_status,
      pipeline_id: project.pipeline_id,
      current_stage_id: project.current_stage_id,
      assigned_sales_rep_id: project.assigned_sales_rep_id,
      assigned_installer_partner_id: project.assigned_installer_partner_id,
      created_at: project.created_at,
      timestamp: new Date().toISOString()
    });
  }

  async sendProjectUpdated(project: any): Promise<void> {
    await this.triggerWebhooks('project_updated', {
      event: 'project_updated',
      project_id: project.id,
      project_name: project.project_name,
      customer_name: project.customer_name,
      customer_email: project.customer_email,
      system_size: project.system_size,
      system_cost: project.system_cost,
      project_status: project.project_status,
      pipeline_id: project.pipeline_id,
      current_stage_id: project.current_stage_id,
      assigned_sales_rep_id: project.assigned_sales_rep_id,
      assigned_installer_partner_id: project.assigned_installer_partner_id,
      updated_at: project.updated_at,
      timestamp: new Date().toISOString()
    });
  }

  async sendProjectDeleted(project: any): Promise<void> {
    await this.triggerWebhooks('project_deleted', {
      event: 'project_deleted',
      project_id: project.id,
      project_name: project.project_name,
      customer_name: project.customer_name,
      timestamp: new Date().toISOString()
    });
  }

  async sendProjectStageChanged(project: any): Promise<void> {
    await this.triggerWebhooks('project_stage_changed', {
      event: 'project_stage_changed',
      project_id: project.id,
      project_name: project.project_name,
      customer_name: project.customer_name,
      old_stage_id: project.previous_stage_id,
      new_stage_id: project.current_stage_id,
      pipeline_id: project.pipeline_id,
      timestamp: new Date().toISOString()
    });
  }

  async sendProjectAssigned(project: any): Promise<void> {
    await this.triggerWebhooks('project_assigned', {
      event: 'project_assigned',
      project_id: project.id,
      project_name: project.project_name,
      customer_name: project.customer_name,
      assigned_sales_rep_id: project.assigned_sales_rep_id,
      timestamp: new Date().toISOString()
    });
  }

  async sendInstallerAssigned(project: any): Promise<void> {
    await this.triggerWebhooks('installer_assigned', {
      event: 'installer_assigned',
      project_id: project.id,
      project_name: project.project_name,
      customer_name: project.customer_name,
      assigned_installer_partner_id: project.assigned_installer_partner_id,
      timestamp: new Date().toISOString()
    });
  }

  async testLatestOnboardingBooking(): Promise<void> {
    try {
      // Get the latest onboarding booking request
      const { data: latestBooking, error } = await supabase
        .from('booking_requests')
        .select(`
          *,
          booking_link:booking_links(*)
        `)
        .eq('booking_link.link_type', 'onboarding_call')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !latestBooking) {
        console.error('No onboarding bookings found:', error);
        throw new Error('No onboarding bookings found to test with');
      }

      // Type-narrow the joined booking_link row so the trigger payload below
      // can read its slug + link_type (PostgREST returns these as a
      // SelectQueryError stub under the loose Database type).
      const bookingRow = latestBooking as unknown as {
        id: string;
        requester_email: string;
        requester_name: string;
        requester_phone: string;
        requested_date: string;
        requested_time_slot: string;
        notes?: string | null;
        meeting_link?: string | null;
        booking_link: { slug: string; link_type: string };
      };

      // Create appointment datetime with timezone info
      const appointmentDateTime = new Date(`${bookingRow.requested_date}T${bookingRow.requested_time_slot}`);
      
      // Send the enhanced webhook payload with the latest booking data
      await this.triggerWebhooks('onboarding_booking_created', {
        event: 'onboarding_booking_created',
        status: 'onboarding-booked',
        timestamp: new Date().toISOString(),
        user_data: {
          email: bookingRow.requester_email,
          name: bookingRow.requester_name,
          phone: bookingRow.requester_phone
        },
        booking_data: {
          booking_id: bookingRow.id,
          booking_link_slug: bookingRow.booking_link.slug,
          requested_date: bookingRow.requested_date,
          requested_time_slot: bookingRow.requested_time_slot,
          appointment_datetime_iso: appointmentDateTime.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timezone_offset: new Date().getTimezoneOffset(),
          notes: bookingRow.notes,
          meeting_link: bookingRow.meeting_link
        },
        metadata: {
          source: 'onboarding_booking',
          link_type: bookingRow.booking_link.link_type,
          test: true
        }
      });

    } catch (error) {
      console.error('Error testing latest onboarding booking webhook:', error);
      throw error;
    }
  }
}

// Create a single instance of the service
const webhookService = WebhookService.getInstance();

// Export the instance
export { webhookService };

// Make it available globally for console testing
if (typeof window !== 'undefined') {
  (window as any).webhookService = webhookService;
} 